// src/common/components/addressForm/useAddresForm.ts
import { useState, useEffect, useCallback } from "react";
import {
  FullAddress,
  ValidationResultInterface,
  ValidationRulesInterface,
} from "../../../common/types/types";
import { getApi } from "../../../api/api";
import { useInput } from "../../hooks/useInput";
import { useValidation } from "../../hooks/useValidation";
import { gMapsAddressHandler } from "../../utils/functions/gMapsAddressHandler";
import { store } from "../../../store/store";
import { setAddresses } from "../../../store/slices/addressSlice";

const initialAddress: FullAddress = {
  name: "",
  description: "",
  isGeneral: false,
  city: { value: "", latitude: "", longitude: "", isDirty: false },
  address: { value: "", latitude: "", longitude: "", isDirty: false },
  entrance: "",
  floor: "",
  apartment: "",
  intercom: "",
};

const addressValidations: ValidationRulesInterface[] = [
  {
    fieldName: "name",
    minLength: 2,
    maxLength: 100,
    canBeEmpty: false,
    isEmptyMessage: "Название адреса обязательно",
    toShortMessage: "Минимум 2 символа",
    tooLongMessage: "Максимум 100 символов",
    formatMessage: "",
  },
  {
    fieldName: "description",
    maxLength: 200,
    canBeEmpty: true,
    tooLongMessage: "Максимум 200 символов",
    isEmptyMessage: "",
    toShortMessage: "",
    formatMessage: "",
  },
  {
    fieldName: "entrance",
    maxLength: 15,
    canBeEmpty: true,
    tooLongMessage: "Максимум 15 символов",
    isEmptyMessage: "",
    toShortMessage: "",
    formatMessage: "",
  },
  {
    fieldName: "floor",
    maxLength: 10,
    canBeEmpty: true,
    tooLongMessage: "Максимум 10 символов",
    isEmptyMessage: "",
    toShortMessage: "",
    formatMessage: "",
  },
  {
    fieldName: "apartment",
    maxLength: 15,
    canBeEmpty: true,
    tooLongMessage: "Максимум 15 символов",
    isEmptyMessage: "",
    toShortMessage: "",
    formatMessage: "",
  },
  {
    fieldName: "intercom",
    maxLength: 10,
    canBeEmpty: true,
    tooLongMessage: "Максимум 10 символов",
    isEmptyMessage: "",
    toShortMessage: "",
    formatMessage: "",
  },
];

export const useAddressForm = (
  getList: () => void,
  onClose?: () => void,
  onSubmit?: (data: FullAddress) => void,
) => {
  const [data, setData] = useState<FullAddress>({ ...initialAddress });
  const [validationResult, setValidationResult] = useState<
    ValidationResultInterface[]
  >([]);
  const [isValid, setIsValid] = useState(false);
  const [addressNotValid, setAddressNotValid] = useState(true);
  const api = getApi();

  const { inputHandler } = useInput(setData);
  const { validate, checkAndAddValidation, setDirty } = useValidation(
    setValidationResult,
    validationResult,
    addressValidations,
  );

  const isAnonymous = useCallback(() => {
    const state = store.getState();
    return state.executor.isExecutor && !state.executor.clientUserId;
  }, []);

  const dataReset = useCallback(
    (fieldName: "city" | "address") => {
      const tempData = { ...data };
      tempData[fieldName].latitude = undefined;
      tempData[fieldName].longitude = undefined;
      tempData[fieldName].value = "";
      tempData[fieldName].isDirty = false;
      setData(tempData);
    },
    [data],
  );

  const formAction = useCallback(async () => {
    try {
      const { isDirty: addrDirty, ...cleanAddress } = data.address;
      const { isDirty: cityDirty, ...cleanCity } = data.city;

      const payload = {
        ...data,
        address: cleanAddress,
        city: cleanCity,
      };

      const anonymous = isAnonymous();
      if (anonymous) {
        (payload as any).userId = null;
        if (!payload.name) {
          payload.name = payload.address?.value?.split(",")[0] || "Адрес";
        }
      }

      const result = await api.createAddress(payload);

      if (anonymous && onSubmit && result) {
        const newAddress = { ...payload, id: result.id } as FullAddress;

        const state = store.getState();
        const currentAddresses = state.addressList.data;
        store.dispatch(setAddresses([...currentAddresses, newAddress]));

        onSubmit(newAddress);
      } else {
        await getList();
      }

      dataReset("city");
      dataReset("address");
      setData({ ...initialAddress });
      if (onClose) onClose();
    } catch (error) {
      console.error("Ошибка сохранения адреса:", error);
    }
  }, [api, data, getList, onClose, dataReset, isAnonymous, onSubmit]);

  const closeForm = useCallback(() => {
    if (onClose) onClose();
    dataReset("city");
    dataReset("address");
    setData({ ...initialAddress });
  }, [dataReset, onClose]);

  const getValidationResult = (fieldName: string) => {
    return validationResult.find((item) => item.name === fieldName);
  };

  const placeHandler = useCallback(
    (place: any, fieldName: "city" | "address") => {
      if (!place?.geometry?.location || !place?.address_components) return;

      const tempData = { ...data };

      tempData[fieldName].latitude = place.geometry.location.lat().toString();
      tempData[fieldName].longitude = place.geometry.location.lng().toString();
      tempData[fieldName].value = gMapsAddressHandler(fieldName, place).text;
      tempData[fieldName].isDirty = true;

      if (fieldName === "address") {
        tempData.city.value = gMapsAddressHandler("city", place).text;
      }
      setData(tempData);
    },
    [data],
  );

  const getValue = useCallback(
    (fieldName: "address" | "city") => {
      return data[fieldName].value;
    },
    [data],
  );

  useEffect(() => {
    Object.keys(data).forEach((field) => {
      if (addressValidations.some((v) => v.fieldName === field)) {
        checkAndAddValidation(field);
      }
    });
  }, [checkAndAddValidation, data]);

  useEffect(() => {
    Object.entries(data).forEach(([field, value]) => {
      if (addressValidations.some((v) => v.fieldName === field)) {
        validate(field, value);
      }
    });
  }, [data, validate]);

  useEffect(() => {
    const addressValid = Boolean(
      data.address.latitude &&
      data.address.longitude &&
      data.city.value &&
      data.address.value,
    );
    setAddressNotValid(!addressValid);
  }, [data]);

  useEffect(() => {
    const anonymous = isAnonymous();
    const allValid = validationResult.every((item) => {
      if (anonymous && item.name === "name") return true;
      return !item.notValid;
    });
    setIsValid(allValid);
  }, [validationResult, isAnonymous]);

  return {
    data,
    validationResult,
    isValid,
    inputHandler,
    setDirty,
    getValidationResult,
    dataReset,
    getValue,
    placeHandler,
    addressNotValid,
    formAction,
    closeForm,
    setData,
  };
};
